import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Cross-domain deployments (Netlify frontend + Railway backend on different domains) get their
// auth cookie silently dropped by mobile Safari/iOS Chrome's Intelligent Tracking Prevention (ITP),
// which blocks third-party cookies by default. To make auth work reliably on mobile, the access
// token is also stored in localStorage and sent as a Bearer header (the backend already supports
// this fallback in auth_utils.get_current_user, checking the header when no cookie is present).
export const AUTH_TOKEN_KEY = "jobvinum_access_token";

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Backend HTTPException detail strings are always in English. This maps the exact known strings
// to i18n keys under "errors.*" so the message shown to the user matches their chosen UI language.
const ERROR_DETAIL_TO_I18N_KEY = {
  "Access denied": "errors.accessDenied",
  "Account blocked": "errors.accountBlocked",
  "Already applied": "errors.alreadyApplied",
  "Already invited for this offer": "errors.alreadyInvited",
  "Application not found": "errors.applicationNotFound",
  "Candidate not found": "errors.candidateNotFound",
  "Contact must be unlocked before rating": "errors.contactMustBeUnlocked",
  "Email already registered": "errors.emailAlreadyRegistered",
  "File not found": "errors.fileNotFound",
  "Forbidden": "errors.forbidden",
  "Insufficient credits": "errors.insufficientCredits",
  "Invalid SIREN, must be 9 digits": "errors.invalidSiren",
  "Invalid document type": "errors.invalidDocumentType",
  "Invalid email or password": "errors.invalidCredentials",
  "Invalid or expired token": "errors.invalidOrExpiredToken",
  "Invalid package": "errors.invalidPackage",
  "Invalid role": "errors.invalidRole",
  "Invalid token type": "errors.invalidOrExpiredToken",
  "Invalid token": "errors.invalidOrExpiredToken",
  "Invitation already processed": "errors.invitationAlreadyProcessed",
  "Invitation not found": "errors.invitationNotFound",
  "Item not found": "errors.itemNotFound",
  "No documents to download": "errors.noDocumentsToDownload",
  "Not authenticated": "errors.notAuthenticated",
  "Not found": "errors.itemNotFound",
  "Offer not available": "errors.offerNotAvailable",
  "Offer not found": "errors.offerNotFound",
  "Only png/jpg images allowed": "errors.onlyImagesAllowed",
  "Only png/jpg/webp images allowed": "errors.onlyImagesAllowed",
  "Post not found": "errors.itemNotFound",
  "Roadmap item not found": "errors.itemNotFound",
  "SIREN lookup service unavailable": "errors.sirenServiceUnavailable",
  "SIREN not found": "errors.sirenNotFound",
  "Token expired": "errors.tokenExpired",
  "Transaction not found": "errors.itemNotFound",
  "User not found": "errors.userNotFound",
  "You already rated this interaction": "errors.alreadyRated",
};

/** Translates a backend error `detail` into the user's chosen language when possible.
 * `t` is the i18next translate function (from useTranslation()); optional for callers that
 * can't easily access it, in which case the raw English string is shown as a fallback. */
export function formatApiErrorDetail(detail, t) {
  if (detail == null) return t ? t("errors.genericError") : "Something went wrong. Please try again.";
  if (typeof detail === "string") {
    const key = ERROR_DETAIL_TO_I18N_KEY[detail];
    return key && t ? t(key) : detail;
  }
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
